"use client"

import React from "react"

import { cn } from "@/lib/utils"

// const _loaderVariants = cva("", {
//   variants: {
//     variant: {
//       aperture: "",
//       gallery: "",
//       filmstrip: "",
//       polaroid: "",
//       lens: "",
//       shutter: "",
//     },
//     size: {
//       xs: "",
//       sm: "",
//       default: "",
//       lg: "",
//       xl: "",
//     },
//     color: {
//       primary: "",
//       secondary: "",
//       accent: "",
//       muted: "",
//       success: "",
//       danger: "",
//     },
//   },
//   defaultVariants: {
//     variant: "gallery",
//     size: "default",
//     color: "primary",
//   },
// })

const sizeMap = {
  xs: { dimension: 20, strokeWidth: 2 },
  sm: { dimension: 28, strokeWidth: 2 },
  default: { dimension: 40, strokeWidth: 3 },
  lg: { dimension: 56, strokeWidth: 4 },
  xl: { dimension: 80, strokeWidth: 5 },
}

const colorMap = {
  primary: "text-primary",
  secondary: "text-secondary",
  accent: "text-accent", 
  muted: "text-muted-foreground",
  success: "text-green-500",
  danger: "text-red-500",
}

export interface LoaderProps {
  variant?: "aperture" | "gallery" | "filmstrip" | "polaroid" | "lens" | "shutter"
  size?: "xs" | "sm" | "default" | "lg" | "xl"
  color?: "primary" | "secondary" | "accent" | "muted" | "success" | "danger"
  className?: string
  label?: string
  fullScreen?: boolean
  overlay?: boolean
}

export function Loader({
  variant = "gallery",
  size = "default",
  color = "primary",
  className,
  label,
  fullScreen = false,
  overlay = false,
}: LoaderProps) {
  const { dimension } = sizeMap[size]
  const colorClass = colorMap[color]

  const loaderContent = (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4",
        fullScreen && "h-screen",
        overlay && "h-full",
        className
      )}
    >
      {variant === "aperture" && (
        <div className="relative" style={{ width: dimension, height: dimension }}>
          {/* Camera aperture blades */}
          <div className="absolute inset-0 aperture-container">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className={cn("aperture-blade", colorClass)}
                style={{
                  transform: `rotate(${i * 45}deg)`,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>
          {/* Center dot */}
          <div className={cn("absolute inset-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current animate-pulse", colorClass)} />
        </div>
      )}

      {variant === "gallery" && (
        <div className="relative" style={{ width: dimension * 1.2, height: dimension }}>
          {/* Photo cards stacking animation */}
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className={cn(
                "absolute rounded-lg border-2 gallery-photo",
                colorClass,
                i === 0 && "z-30",
                i === 1 && "z-20", 
                i === 2 && "z-10"
              )}
              style={{
                width: dimension * 0.8,
                height: dimension * 0.7,
                left: `${i * 8}px`,
                top: `${i * 6}px`,
                animationDelay: `${i * 0.3}s`,
              }}
            >
              <div className={cn("w-full h-full rounded-md bg-gradient-to-br opacity-30", 
                i === 0 ? "from-purple-400 to-pink-400" :
                i === 1 ? "from-blue-400 to-cyan-400" : 
                "from-green-400 to-emerald-400"
              )} />
            </div>
          ))}
        </div>
      )}

      {variant === "filmstrip" && (
        <div className="relative filmstrip-container" style={{ width: dimension * 1.5, height: dimension * 0.8 }}>
          {/* Film strip with moving frames */}
          <div className={cn("filmstrip-track", colorClass)}>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="filmstrip-frame">
                <div className={cn("filmstrip-image bg-gradient-to-br", 
                  i % 3 === 0 ? "from-purple-400 to-pink-400" :
                  i % 3 === 1 ? "from-blue-400 to-cyan-400" : 
                  "from-green-400 to-emerald-400"
                )} />
              </div>
            ))}
          </div>
        </div>
      )}

      {variant === "polaroid" && (
        <div className="relative" style={{ width: dimension * 1.1, height: dimension * 1.2 }}>
          {/* Polaroid developing effect */}
          <div className={cn("polaroid-frame border-4 rounded-lg bg-white", colorClass)}>
            <div className="polaroid-image">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-400 to-orange-400 rounded opacity-80" />
              <div className="polaroid-reveal" />
            </div>
            <div className="polaroid-bottom" />
          </div>
          {/* Sparkles effect */}
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className={cn("absolute w-1 h-1 rounded-full sparkle", colorClass)}
              style={{
                left: `${20 + i * 15}%`,
                top: `${10 + i * 20}%`,
                animationDelay: `${i * 0.5}s`,
              }}
            />
          ))}
        </div>
      )}

      {variant === "lens" && (
        <div className="relative lens-container" style={{ width: dimension, height: dimension }}>
          {/* Camera lens with zoom effect */}
          <div className={cn("lens-outer", colorClass)}>
            <div className="lens-inner">
              <div className="lens-center">
                <div className="lens-reflection" />
              </div>
            </div>
          </div>
          {/* Focus ring */}
          <div className={cn("focus-ring", colorClass)} />
        </div>
      )}

      {variant === "shutter" && (
        <div className="relative shutter-container" style={{ width: dimension, height: dimension }}>
          {/* Camera shutter blades */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={cn("shutter-blade", colorClass)}
              style={{
                transform: `rotate(${i * 60}deg)`,
                animationDelay: `${i * 0.05}s`,
              }}
            />
          ))}
          <div className={cn("shutter-center", colorClass)} />
        </div>
      )}

      {label && (
        <div className="text-center">
          <p className={cn(
            "text-sm font-medium",
            color === "primary" && "text-foreground",
            color === "secondary" && "text-secondary-foreground", 
            color === "accent" && "text-accent-foreground",
            color === "muted" && "text-muted-foreground",
            color === "success" && "text-green-600",
            color === "danger" && "text-red-600"
          )}>
            {label}
          </p>
        </div>
      )}
    </div>
  )

  if (overlay) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        {loaderContent}
      </div>
    )
  }

  return loaderContent
}

export function LoaderButton({
  children,
  loading,
  variant = "gallery",
  size = "xs",
  color = "primary",
  className,
  disabled,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean
} & LoaderProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      disabled={disabled ?? loading}
      {...props}
    >
      {loading && (
        <Loader variant={variant} size={size} color={color} className="!gap-0" />
      )}
      {children}
    </button>
  )
}
